<template lang="pug">
	.web-service--full-structure-parameter-selector(v-if="param")
		.flex-col.space.wrap
			.param-title.flex-1(v-if="paramsHierarchy.length > 1") {{param.Caption || param.Name}}
			.flex-col(v-for="{ param, service, parentService, allowEdit } in editableParamsHierarchy")
				.flex-col(:class="{ 'disabled-controls': !allowEdit }")
					base-parameter-selector(v-if="!param.ParentServiceOid", :parameter="param", :paramValues="params", :paramCaptions="paramsCaptions",
						@change="(values, captions) => onBaseParameterChange({ param, service, values, captions })")
					template(v-else)
						ParentServiceParam.flex-1(:serviceId="param.ParentServiceOid", :name="param.Caption || param.Name", :serviceOutputName="param.ParentServiceOutput", :value="paramValues[param.Name]", :caption="paramsCaptions[param.Name]",
							:forceOutputsSelection="true", :params="params", :paramsCaptions="paramsCaptions"
							@change="(key, item, params, paramsCaptions, propagatedEditableParams) => onParentServiceParamChange({ param, service, key, item, params, paramsCaptions, propagatedEditableParams })")
			Loader(v-if="isLoading", text="")
</template>

<script>
	import _ from 'lodash';
	import serviceHelper from './service-helper';
	import ParentServiceParam from './ParentServiceParam.vue';
	import Loader from 'app-core/Loader.vue';

	export default {
		translationCategory: 'webServiceCaller.StructureBuilder',
		name: 'web-service--full-structure-parameter-selector',
		components: { 
			ParentServiceParam, Loader
		},
		model: {
		},
		props: ['parameter', 'paramResolver', 'paramValues', 'paramCaptions', 'defaultValue', 'checkValidation', 'propagatedEditableParams', 'hideCaption', 'preventShowRootParam'],
		data() {
			return {
				param: this.parameter,
				paramsHierarchy: [],
				params: this.paramValues || {},
				paramsCaptions: this.paramCaptions || {},
				lastUsedParametersPerService: {},
				isLoading: 0,
				preventChangesHanlder: false
			};
		},		
		computed: {
			editableParamsHierarchy() {
				let allowEdit = true;
				return this.paramsHierarchy.filter(f => !this.preventShowRootParam || f.param !== this.param).map((f) => {
					let item = {
						...f,
						allowEdit
					}

					allowEdit = allowEdit && (!f.param.IsRequired || !!this.params[f.param.Name]);

					return item;
				});
			}
		},
		watch: {
			paramValues() {
				this.params = this.paramValues || {};
			},
			paramCaptions() {
				this.paramsCaptions = this.paramCaptions || {};
			}
		},
		mounted() {
			if (__DEBUG__)
				window.fullStructureParamSelector = this;

			this.resolveParam();
		},		
		destroyed() {
			
		},
		methods: {
			onBaseParameterChange({ param, service, values, captions }) {
				if (this.preventChangesHanlder) return;
				if (__DEBUG__)
					console.debug('On base parameter change', { param, service, values, captions });

				this.$set(this.params, param.Name, values && values[param.Name] || null);
				this.onParamChange(param);
				this.saveParams();
			},
			onParentServiceParamChange({ param, service, key, item, params, paramsCaptions, propagatedEditableParams }) {
				if (this.preventChangesHanlder) return;
				if (__DEBUG__)
					console.debug('On parent service parameter change', { param, service, key, item, params, paramsCaptions, propagatedEditableParams });

				this.$set(this.params, param.Name, item?.Key);
				this.$set(this.paramsCaptions, param.Name, item?.Value);
				this.onParamChange(param);
				this.saveParams();
			},
			onParamChange(param) {
				const paramHierarchy = this.paramsHierarchy.find(f => f.param.Name === param.Name);
				this.paramsHierarchy.filter(f => f.level > paramHierarchy.level).map(f => {
					this.$set(this.params, f.param.Name, null);
					if (this.paramsCaptions[f.param.Name]) {
						this.$set(this.paramsCaptions, f.param.Name, null);
					}

					if (__DEBUG__)
						console.debug('onParamChange', { param, f });
				});
			},
			saveParams() {
				this.paramsHierarchy.map(({ param, service }) => {
					if (service) {
						const serviceLastUsedParameters = this.lastUsedParametersPerService[service.Oid];
						if (serviceLastUsedParameters) {
							serviceLastUsedParameters.value.paramValues[param.Name] = this.params[param.Name];
							if (this.paramsCaptions[param.Name]) {
								serviceLastUsedParameters.value.paramCaptions[param.Name] = this.paramsCaptions[param.Name];
							} else {
								delete serviceLastUsedParameters.value.paramCaptions[param.Name];
							}
						}
					}
				});
				this.$emit('change', this.params, this.paramsCaptions);
			},
			fillDefaults() {
				if (!this.paramValues || !this.paramsHierarchy?.length) return;

				this.preventChangesHanlder = true;
				const processedServices = {};
				Promise.all(this.paramsHierarchy.map(async ({ service }) => {
					if (!service || processedServices[service.Oid]) return;

					const editableParameters = await serviceHelper.getEditableParameters(service) || [];
					(service?.JsonDefinition?.ComplexParameters || []).map(f => {
						if (f?.Keys) {
							editableParameters.push(...f?.Keys.map(key => `${f.Name}_${key}`));
						}
						editableParameters.push(f.Name);
					});

					const lastUsedParameters = serviceHelper.getLastUsedParamsForService(service, this.paramValues, this.paramCaptions);
					this.$set(this.lastUsedParametersPerService, service.Oid, lastUsedParameters);
					const usedParams = _.pick(lastUsedParameters?.value?.paramValues, editableParameters) || {};
					const usedParamsCaptions = _.pick(lastUsedParameters?.value?.paramCaptions, editableParameters) || {};
					
					(service.JsonDefinition.Parameters || []).filter(f => editableParameters.includes(f.Name)).map(f => {
						if (this.params[f.Name] === void 0)
							this.$set(this.params, f.Name, usedParams[f.Name] || this.valueResolver(f, f.Value));
						if (usedParamsCaptions[f.Name] && this.paramsCaptions[f.Name] !== usedParamsCaptions[f.Name]) {
							this.$set(this.paramsCaptions, f.Name, usedParamsCaptions[f.Name]);
						}
					});

					processedServices[service.Oid] = true;
				})).finally(() => setImmediate(() => this.preventChangesHanlder = false));
			},
			async resolveParam() {
				if (this.paramResolver && !this.parameter) {
					this.$set(this, 'param', await this.paramResolver());
				}
				this.init();
			},
			async init() {
				this.isLoading++;
				try {
					const paramsHierarchical = [];
					await serviceHelper.getParentEditableParameters(this.param, void 0, void 0, paramsHierarchical);

					let level = 0;
					this.paramsHierarchy = [...paramsHierarchical.reverse().reduce((res, v) => {
						v.map(f => f.level = level);
						res.push(...v);
						level++;
						return res;
					}, []), { param: this.param, service: null, level }];

					this.fillDefaults();

					if (__DEBUG__)
						console.debug('Hierarchical params', this.param, this.paramsHierarchy, paramsHierarchical);

					if (__DEBUG__)
						if (paramsHierarchical.length > 1) {
							window.paramsHierarchy = this;
						}
				} finally {
					this.isLoading--;
				}
			},
			valueResolver(param, value) {
				return this.$Data.externalServiceGetParamValue(param, value);
			},
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.web-service--full-structure-parameter-selector {
		.param-title {
			border-bottom: 1px solid #dbdbdb;
			padding-bottom: 3px;
			text-transform: uppercase;
			color: #d0d0d0;
			font-size: 12px;
		}
	}
</style>
